import React, { useState, useEffect } from 'react';
import './Header.css';
import logo from '../assets/images/logo.png';
import instagramIcon from '../assets/images/instagram.svg';
import tiktokIcon from '../assets/images/tiktok.svg';
import menuPdf from '../assets/Restaurant Food Menu .pdf';

function Header() {
  const [hoveredNav, setHoveredNav] = useState(null);
  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [scale, setScale] = useState(1);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleDownloadMenu = () => {
    // Open the menu PDF in a new tab
    window.open(menuPdf, '_blank');
  };

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    if (targetId === 'order') {
      // Scroll to delivery partners section
      const deliverySection = document.getElementById('delivery-partners');
      if (deliverySection) {
        deliverySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    if (targetId === 'catering') {
      // Scroll to catering section
      const cateringSection = document.getElementById('catering');
      if (cateringSection) {
        cateringSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (targetId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // On mobile: hide header when scrolling down, show when scrolling up or at top
        if (scrollPosition < 10) {
          setIsVisible(true);
          setOpacity(1);
          setScale(1);
        } else if (scrollPosition > lastScrollY && scrollPosition > 100) {
          // Scrolling down - hide header
          setIsVisible(false);
          setOpacity(0);
          setScale(0.9);
        } else if (scrollPosition < lastScrollY) {
          // Scrolling up - show header
          setIsVisible(true);
          setOpacity(1);
          setScale(1);
        }
        setLastScrollY(scrollPosition);
      } else {
        // Desktop: fade and shrink behavior
        setIsVisible(true);
        const fadeStart = 50;
        const fadeEnd = 200;
        
        if (scrollPosition < fadeStart) {
          setOpacity(1);
          setScale(1);
        } else if (scrollPosition > fadeEnd) {
          setOpacity(0.8);
          setScale(0.9);
        } else {
          // Linear interpolation between fadeStart and fadeEnd
          const fadeRange = fadeEnd - fadeStart;
          const scrollRange = scrollPosition - fadeStart;
          const progress = scrollRange / fadeRange;
          setOpacity(1 - progress * 0.2); // Fade from 1 to 0.8
          setScale(1 - progress * 0.1); // Scale from 1 to 0.9
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <header 
      className="header"
      style={{
        ...styles.header,
        opacity: opacity,
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        visibility: isVisible ? 'visible' : 'hidden',
        pointerEvents: isVisible ? 'auto' : 'none'
      }}
    >
      <div style={styles.headerContainer} className="header-container">
        {/* Logo on the left */}
        <div style={styles.headerLogoSection} className="header-logo-section">
          <img 
            src={logo} 
            alt="Indo-Nepal Culinary Fusion Logo" 
            style={styles.headerLogo}
            className="header-logo"
            loading="eager"
            decoding="async"
          />
        </div>

        {/* Navigation in the middle */}
        <nav style={styles.headerNav} className="header-nav">
          {['home', 'menu', 'contact', 'catering', 'order'].map((link) => (
            <a
              key={link}
              href={`#${link}`}
              className="nav-link"
              style={{
                ...styles.navLink,
                ...(hoveredNav === link ? styles.navLinkHover : {})
              }}
              onClick={(e) => handleNavClick(e, link)}
              onMouseEnter={() => setHoveredNav(link)}
              onMouseLeave={() => setHoveredNav(null)}
            >
              {link === 'home' ? 'Home' : link === 'menu' ? 'Menu' : link === 'contact' ? 'Contact Us' : link === 'catering' ? 'Catering' : 'Order Now'}
            </a>
          ))}
        </nav>

        {/* Social media and Download Menu on the right */}
        <div style={styles.headerRightSection} className="header-right-section">
          <div style={styles.socialIcons}>
            <a
              href="https://www.instagram.com/indonepalculinaryfusion?igsh=bTE3YTN3bXE0NDhw"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialIcon,
                ...(hoveredSocial === 'instagram' ? styles.socialIconHover : {})
              }}
              aria-label="Instagram"
              onMouseEnter={() => setHoveredSocial('instagram')}
              onMouseLeave={() => setHoveredSocial(null)}
            >
              <img src={instagramIcon} alt="Instagram" style={styles.socialIconSvg} />
            </a>
            <a
              href="https://www.tiktok.com/@indonepalculinary?_r=1&_t=ZT-91WLfZKejjz"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialIcon,
                ...(hoveredSocial === 'tiktok' ? styles.socialIconHover : {})
              }}
              aria-label="TikTok"
              onMouseEnter={() => setHoveredSocial('tiktok')}
              onMouseLeave={() => setHoveredSocial(null)}
            >
              <img src={tiktokIcon} alt="TikTok" style={styles.socialIconSvg} />
            </a>
          </div>
          <button
            className="download-menu-btn"
            style={{
              ...styles.downloadMenuBtn,
              ...(hoveredButton ? styles.downloadMenuBtnHover : {})
            }}
            onClick={handleDownloadMenu}
            onMouseEnter={() => setHoveredButton(true)}
            onMouseLeave={() => setHoveredButton(false)}
          >
            Download Menu
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;

const styles = {
  header: {
    background: 'white',
    padding: '0.1rem 2rem',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  headerContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '2rem',
  },
  headerLogoSection: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerLogo: {
    maxWidth: '200px',
    maxHeight: '140px',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },
  headerNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    flex: 1,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  navLink: {
    color: '#2c2c2c',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    padding: '0.5rem 0',
    transition: 'color 0.3s ease',
    position: 'relative',
  },
  navLinkHover: {
    color: '#1565c0',
  },
  headerRightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    flexShrink: 0,
  },
  socialIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  socialIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    transition: 'opacity 0.3s ease, filter 0.3s ease',
  },
  socialIconHover: {
    opacity: 0.7,
    filter: 'brightness(0.7)',
  },
  socialIconSvg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  downloadMenuBtn: {
    background: '#1565c0',
    color: '#ffffff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  downloadMenuBtnHover: {
    background: '#0d47a1',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(21, 101, 192, 0.3)',
  },
};
